'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import {
  CheckCircle2,
  Circle,
  Rocket,
  X,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

// ─── Types ─────────────────────────────────────────────

interface ChecklistItem {
  key: string;
  label: string;
  href: string;
  done: boolean;
}

interface ChecklistState {
  items: ChecklistItem[];
  loading: boolean;
  error: string | null;
}

const DISMISS_KEY = 'mc:onboarding-dismissed';

// ─── Data Fetching ─────────────────────────────────────

async function fetchJson<T = unknown>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  return res.json() as Promise<T>;
}

interface HealthResponse {
  heartbeat?: { stripe?: string; [k: string]: unknown };
}
interface ServicesResponse {
  services?: unknown[];
}
interface RatesResponse {
  rates?: unknown[];
}
interface EstimatesResponse {
  estimates?: unknown[];
}
interface VehiclesResponse {
  vehicles?: unknown[];
}

async function probeSetupStatus(): Promise<ChecklistItem[]> {
  // Fire all fetches in parallel — each one is allowed to fail independently
  const [healthRes, servicesRes, ratesRes, estimatesRes, vehiclesRes] =
    await Promise.allSettled([
      fetchJson<HealthResponse>('/api/health'),
      fetchJson<ServicesResponse>('/api/field/services'),
      fetchJson<RatesResponse>('/api/admin/production-rates'),
      fetchJson<EstimatesResponse>('/api/estimates/unified?limit=1'),
      fetchJson<VehiclesResponse>('/api/field/vehicles'),
    ]);

  const health =
    healthRes.status === 'fulfilled' ? healthRes.value : null;
  const services =
    servicesRes.status === 'fulfilled' ? servicesRes.value : null;
  const rates =
    ratesRes.status === 'fulfilled' ? ratesRes.value : null;
  const estimates =
    estimatesRes.status === 'fulfilled' ? estimatesRes.value : null;
  const vehicles =
    vehiclesRes.status === 'fulfilled' ? vehiclesRes.value : null;

  return [
    {
      key: 'services',
      label: 'Configure services',
      href: '/admin/pricing',
      done: (services?.services?.length ?? 0) > 0,
    },
    {
      key: 'stripe',
      label: 'Connect Stripe',
      href: '/admin/integrations',
      done: health?.heartbeat?.stripe === 'healthy',
    },
    {
      key: 'bouncie',
      label: 'Connect Bouncie GPS',
      href: '/admin/integrations',
      done: (vehicles?.vehicles?.length ?? 0) > 0,
    },
    {
      key: 'rates',
      label: 'Set production rates',
      href: '/admin/rates',
      done: (rates?.rates?.length ?? 0) > 0,
    },
    {
      key: 'estimate',
      label: 'Create your first estimate',
      href: '/estimates',
      done: (estimates?.estimates?.length ?? 0) > 0,
    },
  ];
}

// ─── Component ─────────────────────────────────────────

export function OnboardingChecklist() {
  const [state, setState] = useState<ChecklistState>({
    items: [],
    loading: true,
    error: null,
  });
  const [dismissed, setDismissed] = useState<boolean | null>(null);

  // Check localStorage on mount
  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) return;
      try {
        setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
      } catch {
        setDismissed(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch setup status
  useEffect(() => {
    let cancelled = false;
    probeSetupStatus()
      .then((items) => {
        if (!cancelled) setState({ items, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ items: [], loading: false, error: String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage might be unavailable
    }
  }, []);

  // Don't render while loading
  if (state.loading || dismissed === null) return null;

  // Don't render if dismissed
  if (dismissed) return null;

  // Don't render on error (fail silent — this is an enhancement, not critical)
  if (state.error) return null;

  // Don't render if all items are complete
  const doneCount = state.items.filter((i) => i.done).length;
  const totalCount = state.items.length;
  if (totalCount === 0 || doneCount === totalCount) return null;

  return (
    <div className="mt-4 rounded-xl border border-white/5 bg-zinc-900/60 backdrop-blur-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Rocket className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white text-balance">Getting Started</h2>
            <p className="text-[10px] text-zinc-500 font-medium mt-0.5">
              <span className="text-blue-400 tabular-nums">{doneCount}</span>
              {' of '}
              <span className="tabular-nums">{totalCount}</span>
              {' complete'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleDismiss}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-colors"
          aria-label="Dismiss onboarding checklist"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Progress bar */}
      <div className="h-1 rounded-full bg-zinc-800 overflow-hidden mb-4">
        <div
          className="h-full rounded-full bg-blue-500 transition-[width] duration-500 ease-out"
          style={{ width: `${Math.round((doneCount / totalCount) * 100)}%` }}
        />
      </div>

      {/* Checklist */}
      <div className="space-y-1">
        {state.items.map((item) => (
          <div
            key={item.key}
            className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
              item.done
                ? 'bg-transparent'
                : 'bg-white/[0.02] hover:bg-white/[0.04]'
            }`}
          >
            <div className="flex items-center gap-2.5">
              {item.done ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-zinc-600 flex-shrink-0" />
              )}
              <span
                className={`text-xs font-medium ${
                  item.done ? 'text-zinc-500 line-through' : 'text-zinc-200'
                }`}
              >
                {item.label}
              </span>
            </div>
            {!item.done && (
              <Link
                href={item.href}
                className="flex items-center gap-1 text-[10px] font-semibold text-blue-400 hover:text-blue-300 transition-colors whitespace-nowrap"
              >
                Set Up
                <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
